'use client'

import Link from "@/components/ui/Link";
import Button from "../ui/button";
import { sanitizeHTML } from "@/lib/utils";
import { useState, useMemo, useEffect } from "react";

interface FrequentlyQuestionsData {
	anchor_title?: string;
  heading?: {
    text?: string;
    title?: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        item: {
          title: string;
          text: string;
          short_description?: string;
        };
      }[];
    };
  }[];
  buttons?: {
    menu_items_id: {
      href: string;
      label: string;
    };
  }[];
  button?: {
    label?: string;
    href?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  video?: {
    embed_url: string;
    embed_title: string;
  };
  text?: {
    text?: string;
  };
}

type ContentCollection = NonNullable<FrequentlyQuestionsData['content_collection']>;
const EMPTY_CONTENT_COLLECTION: ContentCollection = [];

export const fields = [
	'anchor_title',
	'heading.text',
	'heading.title',

	'subheading.text',
	'subheading.title',

	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',

	'buttons.menu_items_id.href',
	'buttons.menu_items_id.label',

	'text.text',

	'button.label',
	'button.href',

	'video.embed_url',
	'video.embed_title',

	'content_collection.site_content_collections_id.collection.item:site_content.short_description',
] as const

export default function FrequentlyQuestions({
  data,
}: {
  data?: FrequentlyQuestionsData;
}) {
	const sectionIdBase = data?.anchor_title || 'FrequentlyQuestions'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')
	const contentCollection: ContentCollection = data?.content_collection ?? EMPTY_CONTENT_COLLECTION

	// Group items by short_description
	const groupedItems = useMemo(() => {
		if (contentCollection.length === 0) return {};

		const groups: Record<string, ContentCollection> = {};

		contentCollection.forEach((item) => {
			const collection = item.site_content_collections_id?.collection || [];
			const shortDescription = collection.find(c => c.item?.short_description)?.item?.short_description || 'Other';

			if (!groups[shortDescription]) {
				groups[shortDescription] = [];
			}
			groups[shortDescription].push(item);
		});

		return groups;
	}, [contentCollection]);

	// Get list of tabs (unique short_descriptions) only if there are groups with multiple items
	const tabs = useMemo(() => {
		const allTabs = Object.keys(groupedItems);
		// Only show tabs if there's at least one group with 2 or more items
		const hasGroupsWithMultipleItems = allTabs.some(tab => (groupedItems[tab]?.length || 0) > 1);
		
		return hasGroupsWithMultipleItems ? allTabs : [];
	}, [groupedItems]);

	const [activeTab, setActiveTab] = useState<string>(tabs[0] || '');

	// Keep active tab valid when tabs change
	useEffect(() => {
		if (tabs.length === 0 && activeTab) {
			setActiveTab('');
			return;
		}

		if (tabs.length > 0 && !tabs.includes(activeTab)) {
			setActiveTab(tabs[0]);
		}
	}, [tabs, activeTab]);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const normalizedSectionId = sectionId.toLowerCase();
		const hash = decodeURIComponent(window.location.hash.replace('#', '')).toLowerCase();
		const scrollParam = new URLSearchParams(window.location.search).get('scroll')?.toLowerCase() || '';
		const shouldScroll = hash === normalizedSectionId || scrollParam === normalizedSectionId;
		if (!shouldScroll) return;

		let attempts = 0;
		const maxAttempts = 10;

		const scrollToSection = () => {
			const section = document.getElementById(sectionId);
			if (!section) return false;
			section.scrollIntoView({ behavior: 'auto', block: 'start' });
			return true;
		};

		if (scrollToSection()) return;

		const intervalId = window.setInterval(() => {
			attempts += 1;
			if (scrollToSection() || attempts >= maxAttempts) {
				window.clearInterval(intervalId);
			}
		}, 150);

		return () => window.clearInterval(intervalId);
	}, [sectionId]);

	const slugSegment = (s: string) =>
		s.replace(/\s+/g, '-').replaceAll(/[^a-zA-Z0-9-_]/g, '-')

	const renderItems = (
		items: ContentCollection,
		tabKey?: string,
		reactKeyPrefix?: string
	) => {
		const accordionName = tabKey
			? `faq-${sectionId}-${slugSegment(tabKey)}`
			: `faq-${sectionId}`

		return items?.map((item, index) => {
			const collection =
				item.site_content_collections_id?.collection || []
			const title = collection.find(c => c.item?.title)?.item?.title || ''
			const text = collection.find(c => c.item?.text)?.item?.text || ''
			const key = reactKeyPrefix ? `${reactKeyPrefix}-${item.id}` : item.id

			return (
				<details
					key={key}
					name={accordionName}
					className={`group border-black ${
						index === items.length - 1 ? '' : 'border-b'
					}`}
				>
					<summary className='w-full flex justify-between items-center gap-[16.99px] py-[23px] list-none cursor-pointer [&::-webkit-details-marker]:hidden'>
						<div className='flex items-center gap-4'>
							{title && (
								<h3 className='text-2xl text-left'>{title}</h3>
							)}
							<svg
								className='w-[32px] h-[11px] transition-all duration-300 flex-shrink-0 group-open:rotate-90 group-open:opacity-0'
								viewBox='0 0 49 16'
								fill='none'
								xmlns='http://www.w3.org/2000/svg'
								aria-hidden
							>
								<path
									d='M48.695 8.40779C49.0855 8.01727 49.0855 7.3841 48.695 6.99358L42.3311 0.629616C41.9405 0.239091 41.3074 0.239091 40.9168 0.629616C40.5263 1.02014 40.5263 1.65331 40.9168 2.04383L46.5737 7.70068L40.9168 13.3575C40.5263 13.7481 40.5263 14.3812 40.9168 14.7718C41.3074 15.1623 41.9405 15.1623 42.3311 14.7718L48.695 8.40779ZM0.987915 8.70068H47.9879V6.70068H0.987915V8.70068Z'
									fill='black'
								/>
							</svg>
						</div>
					</summary>
					<div className='pb-8 grid gap-4'>
						<div dangerouslySetInnerHTML={sanitizeHTML(text)} />
					</div>
				</details>
			)
		})
	}

	if (contentCollection.length === 0) return null

	return (
		<section id={sectionId} data-name={data?.anchor_title || 'FrequentlyQuestions'} className='py-16 bg-[#f0f0f0]'>
			{data?.subheading && (
				<div
					className='font-freightBig text-sm text-center mt-[10px] capitalize'
					dangerouslySetInnerHTML={sanitizeHTML(
						data.subheading.title || data.subheading.text || ''
					)}
				/>
			)}
			<div className='container'>
				<h2
					className=''
					dangerouslySetInnerHTML={sanitizeHTML(
						data?.heading?.title ||
							data?.heading?.text ||
							'Frequently Asked Questions'
					)}
				/>
				<div className='grid gap-4'>
					{data?.text?.text && (
						<div
							className='mt-[20px] text-center'
							dangerouslySetInnerHTML={sanitizeHTML(data.text.text)}
						/>
					)}
					{data?.video && (
						<div className='container'>
							<iframe
								width='560'
								height='315'
								src={data.video.embed_url}
								title={data.video.embed_title}
								allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
								allowFullScreen
								loading='lazy'
							></iframe>
						</div>
					)}
				</div>

				{tabs.length > 1 && (
					<div className='mt-8 flex flex-wrap justify-center gap-4'>
						{tabs.map((tab) => (
							<button
								key={tab}
								type='button'
								onClick={() => {
									setActiveTab(tab);
								}}
								className={`px-5 py-3 rounded-full text-base font-medium transition-all ${
									activeTab === tab
										? 'bg-black text-white'
										: 'bg-transparent text-black border border-black hover:bg-black/10'
								}`}
							>
								{tab}
							</button>
						))}
					</div>
				)}
			</div>

			<div className='container'>
				<div className='mt-8 space-y-0'>
					{tabs.length > 0 ? (
						tabs.map((tab) => {
							const items = groupedItems[tab] || []
							const isActiveGroup = activeTab === tab

							return (
								<div key={tab} hidden={!isActiveGroup}>
									{renderItems(items, tab, tab)}
								</div>
							)
						})
					) : (
						renderItems(contentCollection)
					)}
				</div>
				{data?.button && (
					<div className='md:flex justify-center mt-8'>
						<Link className='btn' href={data.button.href || '#'}>
							{data.button.label}
						</Link>
					</div>
				)}
				{data?.buttons?.[0]?.menu_items_id?.href && (
					<div className='md:flex justify-center mt-6'>
						<Link className='btn_white' href={data?.buttons?.[0]?.menu_items_id?.href}>
							READ MORE
						</Link>
					</div>
				)}
			</div>
		</section>
	)
}
